import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {ICellRendererParams} from 'ag-grid-community';
import {GridUtils} from '../../../../shared-utilities/utils-old/grid-utils-old/grid-utils';
import {ICellRendererAngularComp} from 'ag-grid-angular';
import {Store} from '@ngrx/store';
import {Observable, Subject} from 'rxjs';
import {IDepartment, ISubDepartment} from '../../../../shared/shared-models/stock/departments';
import {
  selectAllStoreDepartments,
  selectDepAndSubDepForUserSelectedStore,
  selectStoreSettingsForSelectedUserStore,
} from '../../../../features/stock-manager/store/stock-manager.selectors';
import {NGPReport} from '../../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {StockItem} from '../../../../shared-utilities/models-old/datastructures';
import {ISharedStoreSettings} from '../../../../shared/shared-models/firebase/shared-store-settings';
import {getDeepCopyOfObject} from '../../../../shared/shared-utils/object/object.utils';
import {setEditedItem} from '../../../../features/stock-manager/store/stock-manager.actions';
import {selectCurrentNavigationPage} from '../../../../features/user-access/user-stores-store/user-store.selectors';
import {take, takeUntil} from 'rxjs/operators';
import {NavigationPageEnum} from '../../../../features/user-access/models/navigations.enums';
import {selectDepartmentsForSelectedStore} from '../../../../features/ngp-report/store/ngp-report.selectors';
import {
  getDepartmentDisplayName,
  getDepartmentDisplayNameForStore,
  getSubDepartmentDisplayName,
  getSubDepartmentDisplayNameForStore,
  setGridDepartments,
  setGridDepartmentsByStore,
} from '../../../../shared/shared-utils/stock/department-display.utils';

@Component({
    selector: 'app-cell-renderer-departments',
    templateUrl: './cell-renderer-departments.component.html',
    styleUrls: ['./cell-renderer-departments.component.scss'],
    standalone: false
})
export class CellRendererDepartmentsComponent implements ICellRendererAngularComp, OnInit, OnDestroy {

  value: string | number;
  params: ICellRendererParams;
  width: string;
  type: string;
  departments: IDepartment[];
  currentPage: string;
  storeSettings: ISharedStoreSettings;

  currentPage$: Observable<string>;
  departmentsAndSubDepartmentsForCurrentStore$: Observable<IDepartment[]>;
  storeSettings$: Observable<ISharedStoreSettings>;
  subDepartments: ISubDepartment[];

  private destroy$ = new Subject<boolean>();

  constructor(
    private readonly store: Store,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  get isMultiStoreMode(): boolean {
    return this.params?.context?.isMultiStoreMode === true;
  }

  /**
   * Department code/name columns treat an invalid department as row `dep === '0'`.
   * Sub-department still uses this cell's own value.
   */
  get hasValidDepartment(): boolean {
    if (this.params?.colDef?.field === 'subDep') {
      return this.value !== '0';
    }
    if (this.params?.colDef?.field === 'subDepName') {
      return String(this.params?.data?.subDep ?? '') !== '0';
    }
    const departmentCode: string = String(this.params?.data?.dep ?? this.value ?? '');
    return departmentCode !== '0';
  }

  /**
   * Whether this cell has a stored original value that differs from the current value.
   */
  get isEdited(): boolean {
    const original = this.params?.data?.originalValue?.[this.params?.colDef?.field];
    return original !== undefined && original.value !== this.value;
  }

  /**
   * Previous cell value for the undo tooltip. Empty when no original value exists.
   */
  get previousValue(): string {
    const original = this.params?.data?.originalValue?.[this.params?.colDef?.field];
    return original?.value ?? '';
  }

  agInit(params: ICellRendererParams<NGPReport | StockItem, string | number>): void {
    this.params = params;
    this.value = {...params}.value;
    this.type = params.colDef.field;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }


  ngOnInit(): void {
    this.store.select(selectCurrentNavigationPage).pipe(take(1)).subscribe((page: string) => {
      this.currentPage = page;
      this.getDepartments();
    });


    this.storeSettings$ = this.store.select(selectStoreSettingsForSelectedUserStore);
    this.storeSettings$.pipe(takeUntil(this.destroy$)).subscribe((storeSettings: ISharedStoreSettings) => {
      this.storeSettings = storeSettings;
    });
    this.cdr.markForCheck();

  }

  getDepartments(): void {
    if (this.isMultiStoreMode) {
      this.store.select(selectAllStoreDepartments).pipe(takeUntil(this.destroy$))
        .subscribe((departmentsByStore: Record<string, IDepartment[]>): void => {
          setGridDepartmentsByStore(departmentsByStore);
          const storeId: string | undefined = (this.params?.data as StockItem | undefined)?.storeId;
          const departments: IDepartment[] = storeId ? departmentsByStore?.[storeId] ?? [] : [];
          this.departments = departments;
          this.subDepartments = departments.flatMap((dep: IDepartment) => Object.values(dep.subDepartments ?? {}));
          this.cdr.markForCheck();
        });
      return;
    }
    if (this.currentPage === NavigationPageEnum.StockManager) {
      this.departmentsAndSubDepartmentsForCurrentStore$ = this.store.select(selectDepAndSubDepForUserSelectedStore);
    } else {
      this.departmentsAndSubDepartmentsForCurrentStore$ = this.store.select(selectDepartmentsForSelectedStore);
    }

    this.departmentsAndSubDepartmentsForCurrentStore$.pipe(takeUntil(this.destroy$)).subscribe((departments: IDepartment[]): void => {
      this.departments = departments;
      setGridDepartments(departments);
      if (departments) {
        this.subDepartments = departments.flatMap((dep: IDepartment) =>
          Object.values(dep.subDepartments),
        );
      }
      this.cdr.markForCheck();
    });

  }

  checkSubDepCode(): boolean {
    if (this.params.colDef.field === 'subDep' && this.storeSettings?.linkDepartmentsEnabled) {
      if (this.params.data.dep !== '0' && this.departments) {
        const department = this.departments?.find((dep: IDepartment): boolean => dep.department === this.params.data.dep);
        const subDepartment = department?.subDepartments;
        if (subDepartment) {
          const filtered = subDepartment.filter((subDep: ISubDepartment): boolean => subDep.subDepartment === this.value);
          return filtered.length > 0;
        }
      }
      return false;
    } else {
      return true;
    }
  }

  refresh(): boolean {
    return false;
  }


  onUndoButtonClick(): void {
    const field: string | undefined = this.params?.colDef?.field;
    const originalValue = field ? this.params?.data?.originalValue?.[field]?.value : undefined;
    if (!field || originalValue === undefined) {
      return;
    }
    this.params = {...this.params};
    this.params.data = structuredClone(this.params.data);
    this.value = originalValue;
    this.params.value = originalValue;
    this.params.data[field] = originalValue;
    this.checkSubDepCode();
    if (this.currentPage === 'stock-manager') {
      if (this.storeSettings?.linkDepartmentsEnabled) {
        this.params.data.isError = GridUtils.checkSubDepCode(this.params, this.departments);
      }
      GridUtils.removeIsEditingFlagAndDispatch(this.params.data as StockItem, getDeepCopyOfObject(this.params.colDef.field), this.store, this.currentPage);
      GridUtils.updateIsEditedAndForceRefresh(this.params);
      this.store.dispatch(setEditedItem({stockItem: this.params.data}));
    } else {
      if (this.storeSettings?.linkDepartmentsEnabled) {
        GridUtils.updateErrorAndForceRefresh(this.params, this.departments);
      }
      GridUtils.removeIsEditingFlagAndDispatch(this.params.data as NGPReport, getDeepCopyOfObject(this.params.colDef.field), this.store, this.currentPage);
      GridUtils.updateIsEditedAndForceRefresh(this.params);
    }

  }

  getDepartmentName(depCode: string): string {
    if (this.isMultiStoreMode) {
      return getDepartmentDisplayNameForStore((this.params?.data as StockItem | undefined)?.storeId, depCode);
    }
    return getDepartmentDisplayName(depCode);
  }

  getSubDepartmentName(subDepCode: string): string {
    if (this.isMultiStoreMode) {
      return getSubDepartmentDisplayNameForStore((this.params?.data as StockItem | undefined)?.storeId, subDepCode);
    }
    return getSubDepartmentDisplayName(subDepCode, this.departments);
  }

}
